import {currentTimeMillies, _keys, Minute} from "@intuitionrobotics/ts-common";
import {DeviceView, UnitsModule, UnitView} from "@modules/UnitsModule";
import {AndroidArtifactData, InstallationState} from "@app-sp/app-shared/package-manager";
import {AppPackage_ElliQ, AppPackage_TabletElliQ} from "@app/ir-q-app-common/types/push-messages";
import {UserGroupTypes} from "@app/ir-q-app-common/types/units";
import {EnvironmentModule} from "@modules/EnvironmentModule";
import {ActivationsModule} from "@modules/ActivationsModule";
import {isAlive} from "@app/ir-q-app-common/types/runtime-status";
import {Status, VersionStatus} from "./utils";

export const initUnitsType = () => EnvironmentModule.isProd() ? UserGroupTypes.USERS : UserGroupTypes.IR_INTERNAL;

export const getVersionStatus = (unit?: Partial<UnitView>) => {
    if (!unit)
        return VersionStatus.noVersions;

    const {som, tablet} = unit;
    if (!som?.version && !tablet?.version)
        return VersionStatus.noVersions

    if (!som?.version)
        return VersionStatus.noSomVersion

    if (!tablet?.version)
        return VersionStatus.noTabletVersion

    const somVersion = som.version.split("R")[0];
    const tabletVersion = tablet.version.split("R")[0];

    return somVersion === tabletVersion ? VersionStatus.sameVersions : VersionStatus.differentVersions;
}

export const isDeviceRuntimeStatusExist = (deviceView: Partial<DeviceView> | undefined) => {
    return _keys(deviceView || {}).length > 3; // bc one or two fields written by external.
}

export const isDiffVersionMoreThan30Min = (unitId: string, unitView?: Partial<UnitView>) => {
    const pmStatuses = UnitsModule.getPmStatus(unitId);
    if (!pmStatuses || !pmStatuses.length)
        return false;

    let elliqApp: AndroidArtifactData | undefined;
    let rsApp: AndroidArtifactData | undefined;

    pmStatuses.forEach(pmStatus => {
        const findElliqApp = pmStatus.status.artifact_data?.find(artifact => artifact.packageName === AppPackage_ElliQ);
        const findRsApp = pmStatus.status.artifact_data?.find(artifact => artifact.packageName === AppPackage_TabletElliQ);
        if (findElliqApp)
            elliqApp = findElliqApp;

        if (findRsApp)
            rsApp = findRsApp;
    });

    const thirtyMinAgo = currentTimeMillies() - (30 * Minute);
    if (elliqApp && rsApp
        && elliqApp.installationState === InstallationState.InstalledUpToDate && elliqApp.installationState === InstallationState.InstalledUpToDate
        && elliqApp.installationTime < thirtyMinAgo && rsApp.installationTime < thirtyMinAgo
    )
        return getVersionStatus(unitView) === VersionStatus.differentVersions;

    return false;
}

export const prepareStatusValues = (unitId: string, unitView: UnitView): Status => {
    const somInfo = unitView?.som;
    const tabletInfo = unitView?.tablet;
    const unitIsNotUp = !isDeviceRuntimeStatusExist(somInfo) || !isDeviceRuntimeStatusExist(tabletInfo);
    if (ActivationsModule.getActivation(unitId)?.activated && unitIsNotUp)
        return Status.error;

    if (!somInfo && !tabletInfo) {
        return Status.preInstall;
    }

    const isSomAlive = isAlive(somInfo?.timestamp);
    if (!isSomAlive && somInfo?.manualShutdown)
        return Status.manuallyOff;

    const isTabletAlive = isAlive(tabletInfo?.timestamp);
    if (!isSomAlive && !isTabletAlive)
        return Status.down;

    if (!isSomAlive)
        return Status.somOff

    if (!isTabletAlive)
        return Status.tabletOff

    const somOnError = !!unitView.som?.errorCodes;
    const tabletOnError = !!unitView.tablet?.errorCodes;
    if (!somInfo?.manualShutdown && isSomAlive && isTabletAlive && (somOnError || tabletOnError))
        return Status.error;

    if (isDiffVersionMoreThan30Min(unitId, unitView))
        return Status.error;

    return Status.live
};

// ---- per-unit status cache (fleet scale) -----------------------------------
// prepareStatusValues is time-dependent: liveness uses a 5-min buffer and the
// version check a 30-min gate, so a unit's status can change with the clock
// alone. That's why the monitoring pages re-derive status on a timer
// (useLivenessTick), not just on data change. At 2000+ units, re-deriving every
// unit on every tick is wasted work — but a unit whose most recent device
// report is older than FROZEN_AFTER is past BOTH thresholds, so its status is a
// pure function of its (unchanged) data and cannot move until fresh data
// arrives. Fresh data bumps the units-store dataVersion, so we cache such units
// keyed by dataVersion and recompute only the ones that can still change (the
// live / recently-active tail). SELF_HEAL re-derives even a frozen unit every
// few minutes, bounding staleness from any signal that doesn't route through
// dataVersion (e.g. an activations reload).
const FROZEN_AFTER = 35 * Minute; // > the 30-min version gate (and ≫ the 5-min liveness buffer)
const SELF_HEAL = 5 * Minute;

type StatusCacheEntry = { dataVersion: number, computedAt: number, status: Status };
const statusCache = new Map<string, StatusCacheEntry>();

export const prepareStatusValuesCached = (unitId: string, unitView: UnitView, dataVersion: number): Status => {
    const now = currentTimeMillies();
    const mostRecent = Math.max(unitView?.som?.timestamp || 0, unitView?.tablet?.timestamp || 0);
    const frozen = mostRecent > 0 && (now - mostRecent) > FROZEN_AFTER;

    const cached = statusCache.get(unitId);
    if (cached && frozen && cached.dataVersion === dataVersion && (now - cached.computedAt) < SELF_HEAL)
        return cached.status;

    const status = prepareStatusValues(unitId, unitView);
    statusCache.set(unitId, {dataVersion, computedAt: now, status});
    return status;
};

export const handleUnitsTypeFilter = (unitId: string, consideredEnvs: string[]) => {
    const pairComment = UnitsModule.getUnitUserGroup(unitId);
    if (!pairComment || !consideredEnvs.includes(pairComment)) {
        return false;
    }

    return true;
}
